export type AdMode = 'off' | 'review' | 'live';
export type AdPlacement = 'article' | 'airport' | 'calculator';
export const ADSENSE_PUBLISHER_ID = 'ca-pub-8177723553694037';

export interface AdsConfig {
  mode: AdMode;
  publisherId: string;
  publisherVerified: boolean;
  siteApproved: boolean;
  autoAdsDisabled: boolean;
  cmpPublished: boolean;
  cmpWorldwide: boolean;
  /** Exact script URL copied from the account's Privacy & messaging tag. */
  cmpScriptUrl: string;
  slots: Record<AdPlacement, string>;
}

/** Review mode identifies the publisher but never loads advertising or CMP scripts. */
export const adsConfig: Readonly<AdsConfig> = {
  mode: 'review',
  publisherId: ADSENSE_PUBLISHER_ID,
  publisherVerified: false,
  siteApproved: false,
  autoAdsDisabled: false,
  cmpPublished: false,
  cmpWorldwide: false,
  cmpScriptUrl: '',
  slots: { article: '', airport: '', calculator: '' },
};

const placeholderSlots = new Set(['1234567890', '0987654321', '0000000000']);
export function isValidAdSlot(slot: string): boolean {
  return /^\d{10}$/.test(slot) && !placeholderSlots.has(slot);
}

export function hasValidCmpUrl(config: AdsConfig): boolean {
  try {
    const url = new URL(config.cmpScriptUrl);
    return url.protocol === 'https:' && url.hostname === 'fundingchoicesmessages.google.com' &&
      !url.username && !url.password && !url.port && !url.hash &&
      url.pathname === `/i/${config.publisherId.replace(/^ca-/, '')}`;
  } catch { return false; }
}

export function canBootstrapAds(config: AdsConfig = adsConfig): boolean {
  return config.mode === 'live' && /^ca-pub-\d{16}$/.test(config.publisherId) &&
    config.publisherVerified && config.siteApproved && config.autoAdsDisabled &&
    config.cmpPublished && config.cmpWorldwide && hasValidCmpUrl(config) &&
    Object.values(config.slots).some(isValidAdSlot);
}

export function canServePlacement(placement: AdPlacement, config: AdsConfig = adsConfig): boolean {
  return canBootstrapAds(config) && isValidAdSlot(config.slots[placement]);
}

/** Never generate real impressions during local or preview testing. */
export function isLiveAdsOrigin(origin: string): boolean {
  return origin === 'https://airports.ng' || origin === 'https://www.airports.ng';
}
